import { COMPLAINT_STATUS, EVENT_TYPES } from "./constants";

const STATUS_MAP = {
  open: COMPLAINT_STATUS.OPEN,
  replied: COMPLAINT_STATUS.REPLIED,
  closed: COMPLAINT_STATUS.CLOSED,
  penalty_received: COMPLAINT_STATUS.OPEN,
  appealing: COMPLAINT_STATUS.APPEALING,
  appeal_auditing: COMPLAINT_STATUS.REVIEWING,
  appeal_failed: COMPLAINT_STATUS.CLOSED,
  appeal_passed: COMPLAINT_STATUS.APPEAL_SUCCESSFUL,
  penalty_confirmed: COMPLAINT_STATUS.PENALTY_CONFIRMED,
  penalty_executed: COMPLAINT_STATUS.CLOSED,
  penalty_cancelled: COMPLAINT_STATUS.APPEAL_SUCCESSFUL,
  error: COMPLAINT_STATUS.CLOSED,
};

const APPEAL_RESULT_MAP = {
  appealing: "Pending",
  failed: "Rejected",
  passed: "Approved",
  auditing: "Under Review",
};

/** punishOrderStatus: 0=pending, 1=executed, 2=cancelled/revoked */
const getStatusFromPunishDetail = (punishDetail) => {
  if (!punishDetail) return null;

  const appealStatus = String(punishDetail.appealStatus || "").toLowerCase();
  const appealResult = String(punishDetail.appealResult || "").toLowerCase();
  const orderStatus = Number(punishDetail.punishOrderStatus);

  if (
    appealStatus === "passed" ||
    appealResult === "passed" ||
    orderStatus === 2
  ) {
    return COMPLAINT_STATUS.APPEAL_SUCCESSFUL;
  }

  if (
    appealStatus === "failed" ||
    appealResult === "failed" ||
    orderStatus === 1
  ) {
    return COMPLAINT_STATUS.CLOSED;
  }

  if (appealStatus === "auditing") {
    return COMPLAINT_STATUS.REVIEWING;
  }

  if (appealStatus === "appealing") {
    return COMPLAINT_STATUS.APPEALING;
  }

  return null;
};

export const isRichTextEmpty = (html) =>
  !String(html || "")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim();

export const stripHtml = (html) => {
  if (!html) return "";
  return String(html)
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, "&")
    .trim();
};

const parseRawRequest = (rawRequest) => {
  if (!rawRequest) return {};
  if (typeof rawRequest === "string") {
    try {
      return JSON.parse(rawRequest);
    } catch {
      return {};
    }
  }
  return rawRequest;
};

const mapStatus = (status) =>
  STATUS_MAP[String(status || "").toLowerCase()] ||
  status ||
  COMPLAINT_STATUS.OPEN;

const sortByCreatedAt = (items = []) =>
  [...items].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );

const getLatestInboundReply = (replies = []) =>
  [...replies]
    .filter((reply) => reply.direction === "inbound")
    .sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )[0];

const getLatestOutboundReply = (replies = []) =>
  [...replies]
    .filter((reply) => reply.direction === "outbound")
    .sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )[0];

const hasPenaltyConfirmed = (event, replies = []) =>
  event.status === "penalty_confirmed" ||
  replies.some(
    (reply) =>
      reply.direction === "outbound" &&
      (reply.punishDetail?.appealOperate === "CONFIRM" ||
        reply.status === "penalty_confirmed")
  );

const getEffectivePunishDetail = (event, replies = []) => {
  const latestInbound = getLatestInboundReply(replies);
  return latestInbound?.punishDetail || event.punishDetail;
};

const getPenaltyDisplayStatus = (event, replies = []) => {
  if (hasPenaltyConfirmed(event, replies)) {
    return COMPLAINT_STATUS.PENALTY_CONFIRMED;
  }

  const effectiveDetail = getEffectivePunishDetail(event, replies);
  const fromPunishDetail = getStatusFromPunishDetail(effectiveDetail);
  if (fromPunishDetail) {
    return fromPunishDetail;
  }

  const latestOutbound = getLatestOutboundReply(replies);
  const latestInbound = getLatestInboundReply(replies);

  const hasUnresolvedAppeal =
    latestOutbound?.punishDetail?.appealOperate === "APPEAL" &&
    (!latestInbound ||
      new Date(latestOutbound.createdAt) > new Date(latestInbound.createdAt));

  if (hasUnresolvedAppeal) {
    return COMPLAINT_STATUS.REVIEWING;
  }

  if (
    latestInbound?.status &&
    !["open", "appealing"].includes(String(latestInbound.status).toLowerCase())
  ) {
    return mapStatus(latestInbound.status);
  }

  return mapStatus(event.status);
};

const formatTimelineDate = (dateStr) => {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleDateString("en-GB", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const formatTimelineTime = (dateStr) => {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getTimelineMessage = (entry) => {
  if (entry.punishDetail?.appealReplyContent) {
    return stripHtml(entry.punishDetail.appealReplyContent);
  }
  if (entry.punishDetail?.appealOperate === "CONFIRM") {
    return "Penalty accepted";
  }
  if (entry.punishDetail?.appealOperate === "APPEAL") {
    return stripHtml(entry.content) || "Appeal submitted";
  }
  return stripHtml(entry.content);
};

const buildTimelineEntry = (entry, actor) => ({
  id: entry.id,
  date: formatTimelineDate(entry.createdAt),
  time: formatTimelineTime(entry.createdAt),
  actor,
  message: getTimelineMessage(entry),
});

const buildPenaltyTimeline = (event, replies = []) => {
  const timeline = [buildTimelineEntry(event, "Ctrip")];

  sortByCreatedAt(replies).forEach((reply) => {
    timeline.push(
      buildTimelineEntry(
        reply,
        reply.direction === "outbound" ? "Vendor" : "Ctrip"
      )
    );
  });

  return timeline;
};

const buildCustomerServiceTimeline = (event, replies = []) => {
  const timeline = [buildTimelineEntry(event, "Ctrip")];

  sortByCreatedAt(replies).forEach((reply) => {
    timeline.push(
      buildTimelineEntry(
        reply,
        reply.direction === "outbound" ? "Vendor" : "Ctrip"
      )
    );
  });

  return timeline;
};

const mapAttachments = (enclosure) => {
  if (!enclosure) return [];
  const list = Array.isArray(enclosure) ? enclosure : [enclosure];
  return list
    .filter(Boolean)
    .map((file) => ({
      name: file.fileName || file.name || "attachment",
      url: file.fileUrl || file.url || "#",
    }));
};

const formatPenaltyAmount = (punishDetail) => {
  const detail =
    punishDetail?.punishContentDetail?.[0] ||
    punishDetail?.punishContent?.[0] ||
    {};

  const weight = detail.punishWeight ?? detail.punishAmount ?? detail.amount;
  const unit = detail.punishWeightUnit || "CNY";

  if (weight == null || weight === "") return "-";
  return `${weight} ${unit}`;
};

const formatPenaltyReason = (punishDetail, content) => {
  const parts = [
    punishDetail?.firstFoulReasonName,
    punishDetail?.secondFoulReasonName,
  ].filter(Boolean);

  if (parts.length) return parts.join(" | ");
  return stripHtml(content) || "-";
};

const mapAppealHistory = (replies = []) => {
  const sortedReplies = sortByCreatedAt(replies);
  const outboundAppeals = sortedReplies.filter(
    (reply) =>
      reply.direction === "outbound" &&
      reply.punishDetail?.appealOperate === "APPEAL"
  );

  return outboundAppeals.map((appeal, index) => {
    const appealIndex = sortedReplies.findIndex((item) => item.id === appeal.id);
    const followingInbound = sortedReplies
      .slice(appealIndex + 1)
      .find((item) => item.direction === "inbound");

    let status = "Pending";

    if (followingInbound?.punishDetail?.appealResult) {
      status =
        APPEAL_RESULT_MAP[followingInbound.punishDetail.appealResult] ||
        APPEAL_RESULT_MAP[followingInbound.punishDetail.appealStatus] ||
        "Reviewed";
    } else if (followingInbound?.punishDetail?.appealStatus) {
      status =
        APPEAL_RESULT_MAP[followingInbound.punishDetail.appealStatus] ||
        "Reviewed";
    } else if (appeal.status === "appealing") {
      status = "Pending";
    }

    return {
      id: appeal.id || index + 1,
      label: `Appeal #${index + 1}`,
      status,
      submittedAt: appeal.createdAt,
      content: stripHtml(appeal.content),
      response: followingInbound
        ? stripHtml(
            followingInbound.punishDetail?.appealReplyContent ||
              followingInbound.content
          )
        : null,
    };
  });
};

const mapPenalty = (event, replies = []) => {
  const punishDetail = getEffectivePunishDetail(event, replies);
  if (!punishDetail) return null;

  const rawRequest = parseRawRequest(event.rawRequest);
  const isConfirmed = hasPenaltyConfirmed(event, replies);
  const displayStatus = getPenaltyDisplayStatus(event, replies);
  const isAppealSuccessful =
    displayStatus === COMPLAINT_STATUS.APPEAL_SUCCESSFUL;
  const surplus = Number(punishDetail.surplusAppealCount ?? 0);
  const appealableFlag =
    punishDetail.appealable === 1 || punishDetail.appealable === true;

  const latestOutbound = getLatestOutboundReply(replies);
  const latestInbound = getLatestInboundReply(replies);
  const hasPendingAppeal =
    !isConfirmed &&
    !isAppealSuccessful &&
    latestOutbound?.punishDetail?.appealOperate === "APPEAL" &&
    (!latestInbound ||
      new Date(latestOutbound.createdAt) > new Date(latestInbound.createdAt));

  return {
    reason: formatPenaltyReason(punishDetail, event.content),
    firstFoulReason: punishDetail.firstFoulReasonName || "-",
    secondFoulReason: punishDetail.secondFoulReasonName || "-",
    amount: formatPenaltyAmount(punishDetail),
    appealable:
      !isConfirmed &&
      !isAppealSuccessful &&
      appealableFlag &&
      surplus > 0 &&
      !hasPendingAppeal,
    remainingAppeals:
      isConfirmed || isAppealSuccessful ? 0 : Math.max(0, surplus),
    totalAppeals: punishDetail.totalAppealCount ?? 0,
    maxAppeals: punishDetail.maxAppealTime ?? 0,
    appealTime: punishDetail.appealTime ?? 0,
    currentStatus: displayStatus,
    appealStatus: isConfirmed
      ? "confirmed"
      : isAppealSuccessful
        ? "passed"
        : punishDetail.appealStatus || "-",
    appealResult: isConfirmed
      ? "confirmed"
      : isAppealSuccessful
        ? "passed"
        : punishDetail.appealResult || "-",
    appealReplyContent: stripHtml(punishDetail.appealReplyContent) || null,
    noticeType: punishDetail.noticeType,
    punishOrderStatus: punishDetail.punishOrderStatus,
    punishId: event.eventId || rawRequest.ctripEventId,
    punishTargetId: punishDetail.punishTargetId,
    appealId: punishDetail.appealId,
    punishCreateTime: punishDetail.punishCreateTime,
    hasPendingAppeal,
    isConfirmed,
    isAppealSuccessful,
    appealCodeMap: (punishDetail.appealReason || []).reduce((acc, item) => {
      if (item?.labelName) {
        acc[item.labelName] = item.labelCode;
      }
      return acc;
    }, {}),
    appealOptions: (punishDetail.appealReason || []).map((item) => ({
      labelCode: item.labelCode,
      labelName: item.labelName,
      needCertificate: item.needAppealCertificate,
    })),
  };
};

const getCustomerServiceSubject = (event) =>
  event.eventDetail?.eventTypeName ||
  event.eventSubject ||
  "-";

const getPenaltySubject = (punishDetail, content) => {
  if (punishDetail?.firstFoulReasonName) {
    return punishDetail.firstFoulReasonName;
  }
  return stripHtml(content) || "-";
};

export const mapCtripEventToComplaint = (event) => {
  const rawRequest = parseRawRequest(event.rawRequest);
  const replies = event.replies || [];
  const eventSource = event.eventSource;
  const isPenalty = eventSource === 2;
  const punishDetail = event.punishDetail;
  const penalty = isPenalty ? mapPenalty(event, replies) : null;

  return {
    id: event.id,
    ctripEventId: String(event.eventId || ""),
    ctripPurchaseOrderId: String(
      event.purchaseOrderId || rawRequest.ctripPurchaseOrderId || ""
    ),
    fullOrderId: String(
      event.purchaseOrderId || rawRequest.ctripPurchaseOrderId || ""
    ),
    vendorOrderId: event.vendorOrderId || rawRequest.vendorOrderId || "",
    masterOrderId: event.masterOrderId || rawRequest.masterOrderId || "",
    serviceProviderId: String(
      rawRequest.serviceProviderId || event.serviceProviderId || ""
    ),
    serviceProvider: String(
      rawRequest.serviceProviderId || event.serviceProviderId || "-"
    ),
    eventSource,
    eventType: isPenalty ? EVENT_TYPES.PENALTY : EVENT_TYPES.CUSTOMER_SERVICE,
    subject: isPenalty
      ? getPenaltySubject(punishDetail, event.content)
      : getCustomerServiceSubject(event),
    category: isPenalty
      ? formatPenaltyReason(punishDetail, event.content)
      : getCustomerServiceSubject(event),
    status: isPenalty
      ? getPenaltyDisplayStatus(event, replies)
      : mapStatus(event.status),
    rawStatus: event.status,
    deadline: event.deadLine || event.deadline,
    createdAt: event.createdAt,
    complaintText: stripHtml(event.content),
    attachments: mapAttachments(event.enclosure),
    timeline: isPenalty
      ? buildPenaltyTimeline(event, replies)
      : buildCustomerServiceTimeline(event, replies),
    penalty,
    appealHistory: isPenalty ? mapAppealHistory(replies) : [],
    hasReply: replies.some((reply) => reply.direction === "outbound"),
    hasPendingAppeal: penalty?.hasPendingAppeal || false,
    bookingId: event.bookingId,
    booking: event.booking || null,
    eventDetail: event.eventDetail || null,
    replies,
    _raw: event,
  };
};

export const mapCtripEvents = (events = []) =>
  (Array.isArray(events) ? events : []).map(mapCtripEventToComplaint);

export const extractCtripEvents = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

export const isPenaltyClosed = (complaint) => {
  const closedStatuses = [
    COMPLAINT_STATUS.CLOSED,
    COMPLAINT_STATUS.PENALTY_CONFIRMED,
    COMPLAINT_STATUS.APPEAL_SUCCESSFUL,
    "penalty_confirmed",
    "penalty_executed",
    "penalty_cancelled",
    "appeal_passed",
    "appeal_failed",
  ];

  if (
    closedStatuses.includes(complaint.status) ||
    closedStatuses.includes(complaint.rawStatus)
  ) {
    return true;
  }

  const penalty = complaint.penalty;
  if (!penalty) return false;

  return (
    penalty.isConfirmed ||
    penalty.isAppealSuccessful ||
    penalty.appealable === false ||
    penalty.remainingAppeals <= 0 ||
    penalty.appealResult === "failed" ||
    penalty.appealResult === "passed" ||
    penalty.appealResult === "confirmed" ||
    penalty.punishOrderStatus === 1 ||
    penalty.punishOrderStatus === 2
  );
};

export const isCustomerServiceClosed = (complaint) =>
  complaint.status === COMPLAINT_STATUS.CLOSED ||
  complaint.status === COMPLAINT_STATUS.REPLIED ||
  complaint.hasReply;

export const canSubmitPenaltyAction = (complaint) => {
  if (isPenaltyClosed(complaint)) return false;
  if (!complaint.penalty) return false;
  if (complaint.penalty.hasPendingAppeal) return false;
  return complaint.penalty.appealable && complaint.penalty.remainingAppeals > 0;
};
